// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import VisualAcuityHeader from "../VisualAcuityHeader/VisualAcuityHeader";
import VisualAcuityScale from "../VisualAcuityScale/VisualAcuityScale";
import VisualAcuityIndex from "../VisualAcuityIndex/VisualAcuityIndex";

// Styles
import styles from "../visualAcuity.module.css";

// Types
import {
  ExamSectionReportType,
  VisualAcuityChartRowType,
} from "src/services/exams/types";

type BaileyLovieGraphType = {
  examData: ExamSectionReportType;
  isLiveUpdate?: boolean;
};

const BaileyLovieGraph = ({ examData, isLiveUpdate }: BaileyLovieGraphType) => {
  const { t } = useTranslation();

  const getChartTitle = useCallback(
    (chartContrast: number) => {
      // if the contrast isn't in the 0 - 100 range,
      // display error text
      if (
        typeof chartContrast !== "number" ||
        chartContrast > 100 ||
        chartContrast < 0
      ) {
        return "N/A";
      }

      // else return a title based on the contrast value
      if (chartContrast >= 50) {
        return `${t("High Contrast")} (${chartContrast}%)`;
      }
      return `${t("Low Contrast")} (${chartContrast}%)`;
    },
    [t]
  );

  const getChartLetters = useCallback(
    (chartRows: Array<VisualAcuityChartRowType>, chartContrast: number) => {
      let displayedLetters: Array<
        Array<{
          color: string;
          backgroundImage: string;
          backgroundSize: string;
          value: string;
        }>
      > = [];

      chartRows?.forEach((chartRow) => {
        displayedLetters.push(
          chartRow?.letters?.map((char) => ({
            color:
              char?.State === "GuessedCorrectly"
                ? "#13A321"
                : char?.State === "None" || char?.State === "Skipped"
                  ? `#202338${chartContrast}`
                  : "#B21515",
            backgroundImage:
              char?.State === "Skipped"
                ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
                : "none",
            backgroundSize: "8px 8px",
            value: char?.Value,
          }))
        );
      });

      return displayedLetters;
    },
    []
  );

  const scoreString = useMemo(() => {
    let score = "";
    examData?.postProcessing?.processedCharts?.forEach((processedChart) => {
      if (!score.length) {
        score += processedChart?.result?.split(" ")?.[0];
      } else {
        score += ` / ${processedChart?.result?.split(" ")?.[0]}`;
      }
    });

    return score;
  }, [examData?.postProcessing?.processedCharts]);

  const isRowWithScore = useCallback(
    (logMarScale: string, chartIndex: number) => {
      if (isLiveUpdate) {
        return false;
      }

      return scoreString?.split(" / ")?.[chartIndex] === logMarScale;
    },
    [isLiveUpdate, scoreString]
  );

  const isError = useMemo(
    () => !examData?.state?.charts || !examData?.state?.charts?.length,
    [examData?.state?.charts]
  );

  return (
    <Grid
      container
      item
      xs={12}
      display="flex"
      flexWrap="wrap"
      rowGap="20px"
      className={
        examData?.state?.charts && examData?.state?.charts?.length > 1
          ? "retinalogik-va-multichart"
          : undefined
      }
    >
      {isError ? (
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("Error: No patient responses recorded.")}
        </Typography>
      ) : (
        examData?.state?.charts?.map((chart, chartIndex) => {
          const chartRows = chart?.rows ?? [];
          const chartContrast = Number(chart?.contrast) * 100;
          const chartLetters = getChartLetters(chartRows, chartContrast);
          const chartTitle = getChartTitle(chartContrast);

          return (
            <Grid container item key={`chart-${chartIndex}`} xs={12}>
              <VisualAcuityHeader
                chartTitle={chartTitle}
                isLiveUpdate={isLiveUpdate}
              />

              {chartLetters?.map((chartLetter, chartLetterIndex) => (
                <Grid
                  container
                  item
                  key={`chart-letter-${chartLetterIndex}`}
                  sx={{
                    border: isRowWithScore(
                      chartRows?.[chartLetterIndex]?.logMARVisualAcuityScale,
                      chartIndex
                    )
                      ? "3px solid #000"
                      : "3px solid #FFF",
                    borderRadius: "4px",
                  }}
                >
                  {/* Snellen Equivalent values */}
                  <VisualAcuityScale
                    chartRow={chartRows?.[chartLetterIndex]}
                    isLiveUpdate={isLiveUpdate}
                  />

                  <Grid container item xs={6}>
                    <Grid item xs={12} display="flex" justifyContent="center">
                      {chartLetter?.map((letter, letterIndex) => {
                        const value = letter?.value;
                        return (
                          <Grid
                            item
                            key={`letter-${letterIndex}`}
                            sx={{
                              backgroundImage: letter?.backgroundImage,
                              backgroundSize: letter?.backgroundSize,
                              display: "flex",
                              justifyContent: "center",
                              // Smaller letter boxes on narrow screens in
                              // the live update view, freeing up room for
                              // the scale/index columns beside them.
                              height: isLiveUpdate
                                ? { xs: "24px", sm: "32px" }
                                : "32px",
                              width: isLiveUpdate
                                ? { xs: "24px", sm: "32px" }
                                : "32px",
                            }}
                          >
                            <Typography
                              display="inline-block"
                              fontSize={
                                isLiveUpdate
                                  ? { xs: "18px", sm: "24px" }
                                  : "24px"
                              }
                              color={letter?.color}
                              className={styles.eye_chart_font}
                            >
                              {value}
                            </Typography>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>

                  {/* LogMAR and Index values */}
                  <VisualAcuityIndex
                    chartRow={chartRows?.[chartLetterIndex]}
                    index={chartLetterIndex}
                    isLiveUpdate={isLiveUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          );
        })
      )}
    </Grid>
  );
};

export default BaileyLovieGraph;
